import { t } from '@superset-ui/core';

/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
export const convertInteger = (value: string | number) => {
	if (typeof value !== 'number') return parseInt(value, 10) || 0;
	return value;
};

//KR2-47786: Viết hàm check dấu từ một chuỗi %
export const checkSignPercent = (text: string) => {
	if (text == '--%') {
		return 'na';
	}
	// Loại bỏ dấu phẩy từ chuỗi
	const sanitizedText = text.replace(/,/g, '');

	// Sử dụng biểu thức chính quy để trích xuất số từ chuỗi
	const match = sanitizedText.match(/([-+]?\d*\.?\d+)\%/);

	if (match) {
		const numericValue = parseFloat(match[1]);
		if (numericValue < 0) {
			return 'negative';
		} else if (numericValue > 0) {
			return 'positive';
		} else {
			return 'zero';
		}
	} else if (!isNaN(parseFloat(sanitizedText))) {
		// Nếu sanitizedText là một số, mặc định là số dương
		return 'positive';
	} else {
		return '';
	}
};

export function getKVFormatNumber(formData: any, type: string, defaultfunc: any) {
	switch (formData && formData[type]) {
		case 'CR1':
			return (value: any) => formatNumberCR1Full(value);
		case 'CR2':
			return (value: any) => formatNumberCR2(value);
		case 'CR3':
			return (value: any) => formatNumberCR3(value);
		case 'CR5':
			return (value: any) => formatNumberCR5Full(value);
		case 'CR7_2':
			return (value: any) => formatNumberCR7_2(value);
		case 'CR7_5':
			return (value: any) => formatNumberCR7_5(value);
		case 'CR8':
			return (value: any) => formatNumberCR8(value);
		default:
			// Mặc định trả về một hàm trống nếu giá trị type không khớp
			return defaultfunc ? defaultfunc : (value: any) => {};
	}
}

export function getKVFormatNumberFunc(functype: string, defaultfunc: any) {
	switch (functype) {
		case 'CR1':
			return (value: any) => formatNumberCR1Full(value);
		case 'CR2':
			return (value: any) => formatNumberCR2(value);
		case 'CR3':
			return (value: any) => formatNumberCR3(value);
		case 'CR5':
			return (value: any) => formatNumberCR5Full(value);
		case 'CR7_2':
			return (value: any) => formatNumberCR7_2(value);
		case 'CR7_5':
			return (value: any) => formatNumberCR7_5(value);
		case 'CR8':
			return (value: any) => formatNumberCR8(value);
		default:
			// Mặc định trả về một hàm trống nếu giá trị type không khớp
			return defaultfunc ? defaultfunc : (value: any) => {};
	}
}

//Trả về value
export function getKVFormatNumberValue(formData: any, type: string, defaultvalue: any, value: any) {
	switch (formData && formData[type]) {
		case 'CR1':
			return formatNumberCR1Full(value);
		case 'CR2':
			return formatNumberCR2(value);
		case 'CR3':
			return formatNumberCR3(value);
		case 'CR5':
			return formatNumberCR5Full(value);
		case 'CR7_2':
			return formatNumberCR7_2(value);
		case 'CR7_5':
			return formatNumberCR7_5(value);
		case 'CR8':
			return formatNumberCR8(value);
		default:
			return defaultvalue;
	}
}

//KR2-47786: Viết tạm hàm convert format để dùng
//Quy tắc 1: Hiện thị tiền
export const formatNumberCR1 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	const isZero = (num: number) => num === 0;

	const formatNumber = (num: number) => {
		//Convert sang số làm tròn 2 chữ số sau dấu thập phân
		const numString = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).toString();

		// Tìm vị trí của dấu chấm (nếu có)
		const dotIndex = numString.indexOf('.');

		// Nếu không có dấu chấm, trả về nguyên số
		if (dotIndex === -1) {
			const integerPart = numString;
			const decimalPart = ''; // Không có phần thập phân

			return { integerPart, decimalPart };
		}

		// Nếu có dấu chấm, xử lý cả phần nguyên và phần thập phân
		let integerPart = numString.slice(0, dotIndex);
		let decimalPart = numString.slice(dotIndex + 1);

		// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
		decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng

		integerPart = formatNumberCR2(Number(integerPart));
		return { integerPart, decimalPart };
	};

	const applyFormat = (integerPart: string, decimalPart: string, unit: string) => {
		const formattedValue = decimalPart && decimalPart !== '0' ? `${integerPart}.${decimalPart}` : integerPart;

		return {
			value: decimalPart !== '' ? formattedValue : integerPart,
			unit: unit,
		};
	};

	const getUnit = (magnitude: number) => {
		if (magnitude >= 9) {
			return { value: 9, unit: t('billion') };
		} else if (magnitude >= 6) {
			return { value: 6, unit: t('million') };
		} else if (magnitude >= 3) {
			return { value: 3, unit: t('thousand') };
		} else {
			return { value: 0, unit: '' };
		}
	};

	const formatNumberWithUnit = (number: number) => {
		if (isZero(number)) {
			return applyFormat('0', '0', '');
		}

		const magnitude = Math.floor(Math.log10(Math.abs(number)) / 3) * 3;
		const { value: newMagnitude, unit } = getUnit(magnitude);
		const { integerPart, decimalPart } = formatNumber(number / Math.pow(10, newMagnitude));

		return applyFormat(integerPart, decimalPart, unit);
	};

	return formatNumberWithUnit(number);
};

//KR2-47786: Viết tạm hàm convert format để dùng
//Quy tắc 1: Hiện thị tiền, tương tự hàm formatNumberCR1 nhưng sẽ trả về một string thay vì một object
export const formatNumberCR1Full = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	const isZero = (num: number) => num === 0;

	const formatNumber = (num: number) => {
		//Convert sang số làm tròn 2 chữ số sau dấu thập phân
		const numString = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).toString();

		// Tìm vị trí của dấu chấm (nếu có)
		const dotIndex = numString.indexOf('.');

		// Nếu không có dấu chấm, trả về nguyên số
		if (dotIndex === -1) {
			const integerPart = numString;
			const decimalPart = ''; // Không có phần thập phân

			return { integerPart, decimalPart };
		}

		// Nếu có dấu chấm, xử lý cả phần nguyên và phần thập phân
		let integerPart = numString.slice(0, dotIndex);
		let decimalPart = numString.slice(dotIndex + 1);

		// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
		decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng

		integerPart = formatNumberCR2(Number(integerPart));
		return { integerPart, decimalPart };
	};

	const applyFormat = (integerPart: string, decimalPart: string, unit: string) => {
		const formattedValue = decimalPart && decimalPart !== '0' ? `${integerPart}.${decimalPart}` : integerPart;

		return unit ? `${formattedValue} ${unit}` : formattedValue;
	};

	const getUnit = (magnitude: number) => {
		if (magnitude >= 9) {
			return { value: 9, unit: t('billion') };
		} else if (magnitude >= 6) {
			return { value: 6, unit: t('million') };
		} else if (magnitude >= 3) {
			return { value: 3, unit: t('thousand') };
		} else {
			return { value: 0, unit: '' };
		}
	};

	const formatNumberWithUnit = (number: number) => {
		if (isZero(number)) {
			return applyFormat('0', '0', '');
		}

		const magnitude = Math.floor(Math.log10(Math.abs(number)) / 3) * 3;
		const { value: newMagnitude, unit } = getUnit(magnitude);
		const { integerPart, decimalPart } = formatNumber(number / Math.pow(10, newMagnitude));

		return applyFormat(integerPart, decimalPart, unit);
	};

	return formatNumberWithUnit(number);
};

//Quy tắc 2: Hiện thị số nguyên
export const formatNumberCR2 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}

	// Làm tròn số và chuyển thành chuỗi
	const roundedNum = Math.round(number).toString();

	// Tách thành từng phần
	const parts = roundedNum.split('.');
	const integerPart = parts[0];

	// Thêm dấu phân cách hàng nghìn
	const formattedInteger = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	return formattedInteger;
};

//Quy tắc 3: Hiện thị số thập phân
export const formatNumberCR3 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	// Làm tròn số với 2 chữ số thập phân và chuyển về chuỗi
	const roundedNumberString = number % 1 === 0 ? number.toFixed(0) : number.toFixed(2).toString();

	// Tách phần nguyên và phần thập phân
	let [integerPart, decimalPart] = roundedNumberString.split('.');

	// Thêm dấu phẩy phân cách giữa mỗi 3 đơn vị trong phần nguyên
	const formattedIntegerPart = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
	decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng
	const trimmedNumberString = decimalPart && decimalPart !== '0' ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;

	// Kiểm tra số dương hay âm và thêm dấu tương ứng: trường hợp số âm thì sign sẽ empty vì number đã có sẵn dấu -
	const sign = number === 0 ? '' : number > 0 ? '+' : '';

	// Ghép dấu, dấu chấm, và thêm ký tự "%" vào cuối chuỗi
	const formattedNumber = `${sign}${trimmedNumberString}%`;

	return formattedNumber;
};

//Quy tắc 3: Hiện thị số thập phân, không hiện dấu cộng với số dương
export const formatNumberCR3_2 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	// Làm tròn số với 2 chữ số thập phân và chuyển về chuỗi
	const roundedNumberString = number % 1 === 0 ? number.toFixed(0) : number.toFixed(2).toString();

	// Tách phần nguyên và phần thập phân
	let [integerPart, decimalPart] = roundedNumberString.split('.');

	// Thêm dấu phẩy phân cách giữa mỗi 3 đơn vị trong phần nguyên
	const formattedIntegerPart = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
	decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng
	const trimmedNumberString = decimalPart && decimalPart !== '0' ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;

	// Ghép dấu, dấu chấm, và thêm ký tự "%" vào cuối chuỗi
	const formattedNumber = `${trimmedNumberString}%`;

	return formattedNumber;
};

//Quy tắc 5: Hiện thị số tiền theo dạng rút gọn
export const formatNumberCR5 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	const isZero = (num: number) => num === 0;

	const formatNumber = (num: number) => {
		//Convert sang số làm tròn 2 chữ số sau dấu thập phân
		const numString = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).toString();

		// Tìm vị trí của dấu chấm (nếu có)
		const dotIndex = numString.indexOf('.');

		// Nếu không có dấu chấm, trả về nguyên số
		if (dotIndex === -1) {
			let integerPart = numString;
			const decimalPart = ''; // Không có phần thập phân
			integerPart = formatNumberCR2(Number(integerPart));
			return { integerPart, decimalPart };
		}

		// Nếu có dấu chấm, xử lý cả phần nguyên và phần thập phân
		let integerPart = numString.slice(0, dotIndex);
		let decimalPart = numString.slice(dotIndex + 1);

		// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
		decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng

		integerPart = formatNumberCR2(Number(integerPart));
		return { integerPart, decimalPart };
	};

	const applyFormat = (integerPart: string, decimalPart: string, unit: string) => {
		const formattedValue = decimalPart && decimalPart !== '0' ? `${integerPart}.${decimalPart}` : integerPart;

		return {
			value: decimalPart !== '' ? formattedValue : integerPart,
			unit: unit,
		};
	};

	const getUnit = (magnitude: number) => {
		if (magnitude >= 9) {
			return { value: 9, unit: t('B') };
		} else if (magnitude >= 6) {
			return { value: 6, unit: t('M') };
		} else if (magnitude >= 3) {
			return { value: 3, unit: t('K') };
		} else {
			return { value: 0, unit: '' };
		}
	};

	const formatNumberWithUnit = (number: number) => {
		if (isZero(number)) {
			return applyFormat('0', '0', '');
		}
		const magnitude = Math.floor(Math.log10(Math.abs(number)) / 3) * 3;
		const { value: newMagnitude, unit } = getUnit(magnitude);
		const { integerPart, decimalPart } = formatNumber(number / Math.pow(10, newMagnitude));

		return applyFormat(integerPart, decimalPart, unit);
	};

	return formatNumberWithUnit(number);
};

//Quy tắc 5: Hiện thị tiền, tương tự hàm formatNumberCR5 nhưng sẽ trả về một string thay vì một object
export const formatNumberCR5Full = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	const isZero = (num: number) => num === 0;

	const formatNumber = (num: number) => {
		//Convert sang số làm tròn 2 chữ số sau dấu thập phân
		const numString = num % 1 === 0 ? num.toFixed(0) : num.toFixed(2).toString();

		// Tìm vị trí của dấu chấm (nếu có)
		const dotIndex = numString.indexOf('.');

		// Nếu không có dấu chấm, trả về nguyên số
		if (dotIndex === -1) {
			let integerPart = numString;
			const decimalPart = ''; // Không có phần thập phân
			integerPart = formatNumberCR2(Number(integerPart));
			return { integerPart, decimalPart };
		}

		// Nếu có dấu chấm, xử lý cả phần nguyên và phần thập phân
		let integerPart = numString.slice(0, dotIndex);
		let decimalPart = numString.slice(dotIndex + 1);

		// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
		decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng

		integerPart = formatNumberCR2(Number(integerPart));
		return { integerPart, decimalPart };
	};

	const applyFormat = (integerPart: string, decimalPart: string, unit: string) => {
		const formattedValue = decimalPart && decimalPart !== '0' ? `${integerPart}.${decimalPart}` : integerPart;

		return unit ? `${formattedValue}${unit}` : formattedValue;
	};

	const getUnit = (magnitude: number) => {
		if (magnitude >= 9) {
			return { value: 9, unit: t('B') };
		} else if (magnitude >= 6) {
			return { value: 6, unit: t('M') };
		} else if (magnitude >= 3) {
			return { value: 3, unit: t('K') };
		} else {
			return { value: 0, unit: '' };
		}
	};

	const formatNumberWithUnit = (number: number) => {
		if (isZero(number)) {
			return applyFormat('0', '0', '');
		}

		const magnitude = Math.floor(Math.log10(Math.abs(number)) / 3) * 3;
		const { value: newMagnitude, unit } = getUnit(magnitude);
		const { integerPart, decimalPart } = formatNumber(number / Math.pow(10, newMagnitude));

		return applyFormat(integerPart, decimalPart, unit);
	};

	return formatNumberWithUnit(number);
};

//Quy tắc 7_2: Hiện thị số nguyên cho dynamic, sử dụng hàm formatNumberCR2 để format min max
export const formatNumberCR7_2 = (inputString: string) => {
	if (!inputString) {
		return inputString;
	}
	const parseRange = (rangeString: string): any => {
		const matches = rangeString.match(/(\d+)\D+(\d+)/);
		if (matches) {
			return {
				min: parseInt(matches[1], 10),
				max: parseInt(matches[2], 10),
			};
		}
		return null;
	};

	const parseInput = (inputString: string) => {
		const greaterThanOrEqualRegex = /(?:>=|≥)\s*(\d+)/;
		const greaterThanRegex = />\s*(\d+)/;
		const rangeRegex = /(\d+)\s*-\s*(\d+)/;
		const lessThanRegex = /<\s*(\d+)/;
		const lessThanOrEqualRegex = /(?:<=|≤)\s*(\d+)/;

		let parsedValue;

		if (greaterThanOrEqualRegex.test(inputString)) {
			parsedValue = inputString.match(greaterThanOrEqualRegex);
			return parsedValue ? `≥ ${formatNumberCR2(parseInt(parsedValue[1], 10))}` : null;
		} else if (greaterThanRegex.test(inputString)) {
			parsedValue = inputString.match(greaterThanRegex);
			return parsedValue ? `> ${formatNumberCR2(parseInt(parsedValue[1], 10))}` : null;
		} else if (rangeRegex.test(inputString)) {
			parsedValue = inputString.match(rangeRegex);
			const range = parseRange(inputString);
			return range ? `${formatNumberCR2(parseInt(range.min, 10))} - ${formatNumberCR2(parseInt(range.max, 10))}` : null;
		} else if (lessThanRegex.test(inputString)) {
			parsedValue = inputString.match(lessThanRegex);
			return parsedValue ? `< ${formatNumberCR2(parseInt(parsedValue[1], 10))}` : null;
		} else if (lessThanOrEqualRegex.test(inputString)) {
			parsedValue = inputString.match(lessThanOrEqualRegex);
			return parsedValue ? `≤ ${formatNumberCR2(parseInt(parsedValue[1], 10))}` : null;
		}

		return formatNumberCR2(parseInt(inputString));
	};

	const roundedValue = parseInput(inputString);

	return roundedValue;
};

//Quy tắc 7_5: Hiện thị tiền rút gọn cho dynamic, sử dụng hàm formatNumberCR5 để format min max
export const formatNumberCR7_5 = (inputString: string) => {
	if (!inputString) {
		return inputString;
	}
	const parseRange = (rangeString: string): any => {
		const matches = rangeString.match(/(\d+)\D+(\d+)/);
		if (matches) {
			return {
				min: parseInt(matches[1], 10),
				max: parseInt(matches[2], 10),
			};
		}
		return null;
	};

	const parseInput = (inputString: string) => {
		const greaterThanOrEqualRegex = /(?:>=|≥)\s*(\d+)/;
		const greaterThanRegex = />\s*(\d+)/;
		const rangeRegex = /(\d+)\s*-\s*(\d+)/;
		const lessThanRegex = /<\s*(\d+)/;
		const lessThanOrEqualRegex = /(?:<=|≤)\s*(\d+)/;

		let parsedValue;

		if (greaterThanOrEqualRegex.test(inputString)) {
			parsedValue = inputString.match(greaterThanOrEqualRegex);
			return parsedValue ? `≥ ${formatNumberCR5Full(parseInt(parsedValue[1], 10))}` : null;
		} else if (greaterThanRegex.test(inputString)) {
			parsedValue = inputString.match(greaterThanRegex);
			return parsedValue ? `> ${formatNumberCR5Full(parseInt(parsedValue[1], 10))}` : null;
		} else if (rangeRegex.test(inputString)) {
			parsedValue = inputString.match(rangeRegex);
			const range = parseRange(inputString);
			return range ? `${formatNumberCR5Full(parseInt(range.min, 10))} - ${formatNumberCR5Full(parseInt(range.max, 10))}` : null;
		} else if (lessThanRegex.test(inputString)) {
			parsedValue = inputString.match(lessThanRegex);
			return parsedValue ? `< ${formatNumberCR5Full(parseInt(parsedValue[1], 10))}` : null;
		} else if (lessThanOrEqualRegex.test(inputString)) {
			parsedValue = inputString.match(lessThanOrEqualRegex);
			return parsedValue ? `≤ ${formatNumberCR5Full(parseInt(parsedValue[1], 10))}` : null;
		}

		return formatNumberCR5Full(parseInt(inputString));
	};

	const roundedValue = parseInput(inputString);

	return roundedValue;
};

//Quy tắc 8: Hiện thị số thập phân, không có %
export const formatNumberCR8 = (number: number) => {
	if (typeof number !== 'number') {
		// Xử lý trường hợp khi giá trị không phải là số
		return number;
	}
	// Làm tròn số với 2 chữ số thập phân và chuyển về chuỗi
	const roundedNumberString = number % 1 === 0 ? number.toFixed(0) : number.toFixed(2).toString();

	// Tách phần nguyên và phần thập phân
	let [integerPart, decimalPart] = roundedNumberString.split('.');

	// Thêm dấu phẩy phân cách giữa mỗi 3 đơn vị trong phần nguyên
	const formattedIntegerPart = integerPart?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	// Loại bỏ số 0 không cần thiết ở phần thập phân nếu có
	decimalPart = decimalPart?.replace(/0+$/, ''); // Loại bỏ số 0 cuối cùng
	const trimmedNumberString = decimalPart && decimalPart !== '0' ? `${formattedIntegerPart}.${decimalPart}` : formattedIntegerPart;

	// Ghép dấu, dấu chấm, và thêm ký tự "%" vào cuối chuỗi
	const formattedNumber = `${trimmedNumberString}`;

	return formattedNumber;
};

//Trả về số nguyên làm tròn đến 2 chữ số thập phân
export const roundToTwoDigits = (rawDecimalPart: number) => {
	// // Xác định số chữ số
	// let numberOfDigits = Math.floor(Math.log10(rawDecimalPart)) + 1;

	//Nhân với 100 để lấy thêm 2 số sau số thập phân
	rawDecimalPart = rawDecimalPart * 100;

	//Làm tròn thành số nguyên
	rawDecimalPart = Math.round(rawDecimalPart);

	// Làm tròn đến hàng trăm
	let roundedValue = rawDecimalPart / 100;

	return roundedValue;
};
